import React, {useCallback, useContext, useMemo} from 'react';
import Button from '../button';
import {FormContext} from '../form';

import './FormControls.css';

const displayName = 'FormControls';

function FormControls () {
  const {
    advanceActiveStep,
    goToPreviousStep,
    isActiveStepValid,
    lastCompletedStepIndex,
    stepIndex,
    totalNumberOfSteps
  } = useContext(FormContext);

  const isNavigateBackDisabled = useMemo(() => {
    const isDisabled = stepIndex === 1;

    return isDisabled;
  }, [stepIndex]);

  /*
    lastCompletedStepIndex alone isn't enough: it only stops this control from
    reaching past a step nobody has validly finished yet. It says nothing about the
    step sitting on screen right now -- reached by going back, its required field
    since cleared -- which is exactly the case isActiveStepValid covers. R11: without
    it this button walked past that step with nothing filled in, silently dropping
    its values instead of ever saving them.
  */
  const isNavigateForwardDisabled = useMemo(() => {
    const isLastStep = stepIndex === totalNumberOfSteps - 1;
    const isLastCompletedStep = stepIndex === lastCompletedStepIndex;
    const isDisabled = isLastCompletedStep || isLastStep || !isActiveStepValid;

    return isDisabled;
  }, [isActiveStepValid, lastCompletedStepIndex, stepIndex, totalNumberOfSteps]);

  /*
    Routed through the active step's own handleOnGoNext (advanceActiveStep) rather
    than the form's raw goToNextStep, so this control leaves a step exactly the way
    its OK button would: validated, saved into formValues, and reported to analytics.
  */
  const handleOnGoNext = useCallback(() => {
    if (!isNavigateForwardDisabled) {
      advanceActiveStep();
    }
  }, [advanceActiveStep, isNavigateForwardDisabled]);

  const handleOnGoBack = useCallback(() => {
    if (!isNavigateBackDisabled) {
      goToPreviousStep();
    }
  }, [goToPreviousStep, isNavigateBackDisabled]);

  if (stepIndex > 0) {
    return (
      <footer className="form-controls">
        <section className="form-controls__progress">
          <span>
            Step
          </span>

          <strong>
            {stepIndex}
          </strong>

          <span>out of</span>

          <strong>
            {totalNumberOfSteps}
          </strong>
        </section>

        <section className="form-controls__buttons">
          <Button
            className="form-controls__button"
            disabled={isNavigateBackDisabled}
            onClick={handleOnGoBack}
            size={Button.SIZE.SMALL}
          >
            <svg className="form-controls__button__icon" viewBox="0 0 448 512">
              <path fill="currentColor" d="M240.971 130.524l194.343 194.343c9.373 9.373 9.373 24.569 0 33.941l-22.667 22.667c-9.357 9.357-24.522 9.375-33.901.04L224 227.495 69.255 381.516c-9.379 9.335-24.544 9.317-33.901-.04l-22.667-22.667c-9.373-9.373-9.373-24.569 0-33.941L207.03 130.525c9.372-9.373 24.568-9.373 33.941-.001z" />
            </svg>
          </Button>

          <Button
            className="form-controls__button"
            disabled={isNavigateForwardDisabled}
            onClick={handleOnGoNext}
            size={Button.SIZE.SMALL}
          >
            <svg className="form-controls__button__icon" viewBox="0 0 448 512">
              <path fill="currentColor" d="M207.029 381.476L12.686 187.132c-9.373-9.373-9.373-24.569 0-33.941l22.667-22.667c9.357-9.357 24.522-9.375 33.901-.04L224 284.505l154.745-154.021c9.379-9.335 24.544-9.317 33.901.04l22.667 22.667c9.373 9.373 9.373 24.569 0 33.941L240.971 381.476c-9.373 9.372-24.569 9.372-33.942 0z" />
            </svg>
          </Button>
        </section>
      </footer>
    );
  }

  return null;
}

FormControls.displayName = displayName;

export default FormControls;
